import { useEffect, useState } from "react";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
// import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import { Grid } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";

import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ErrorIcon from "@mui/icons-material/Error";

import { updateStatusRefund } from "src/api/cancelOrder";

import font from "src/styles/font";
import device from "src/styles/device";
import { Black, DarkGrey, GreyStroke, Primary, Yellow } from "src/styles/color";
import { checkNotEmpty, convertIntToCurrency } from "src/common";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import UploadImage from "src/components/upload/dropzone/UploadImage";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 16px;
    right: 16px;
    color: ${Black};
  }
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
  line-height: 24px;
  color: ${Black};

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const SpanFocusPrice = styled.div`
  font-size: ${font.size.xl};
  font-weight: 600;
  line-height: 32px;
  color: ${Primary};

  span {
    font-size: ${font.size.md};
    font-weight: 400;
    color: ${Black};
  }

  @media ${device.mobileL} {
    font-size: ${font.size.lg};

    span {
      font-size: ${font.size.sm};
    }
  }
`;

const SpanRemark = styled.div`
  color: ${DarkGrey};
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;
  /* margin-bottom: 8px; */

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
  }
`;

const WrapperBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${device.mobileL} {
    /* flex-direction: ${({ row }) => (row ? "row" : "column")};
    align-items: ${({ row }) => (row ? "center" : "flex-start")};
    gap: 4px; */

    .MuiFormControl-root {
      /* width: 100%; */
      width: 50%;
      min-width: unset;
      max-width: unset;
      min-height: 40px;
    }
  }
`;

const StyledDatePicker = styled(DatePicker)`
  .MuiOutlinedInput-root {
    padding-right: 12px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }
`;

const UploadBox = styled.div`
  width: 100%;
  padding-top: 8px;

  @media ${device.mobileL} {
    padding-top: 0;
  }
`;

const WrapperUpload = styled.div`
  width: 100%;
  max-width: 350px;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const WrapperUploadImage = styled.div`
  /* max-width: 300px;
  height: auto;
  max-height: 300px; */
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WrapperImgSlip = styled.div`
  width: 100%;
  max-width: 300px;
  height: auto;
`;

const ImgSlip = styled.img`
  width: 100%;
  height: 100%;
  /* max-height: 300px; */
  object-fit: contain;
`;

const WarningText = styled.div`
  width: 100%;
  /* margin-top: 12px; */
  display: flex;
  justify-content: center;
  gap: 8px;

  span {
    padding-top: 3px;
    font-size: ${font.size.xs};
    color: ${DarkGrey};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const RefundSuccessDialog = ({
  orderDetail,
  onClose,
  onSuccess,
  setIsLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const defaultData = {
    paymentProof: "", // slip image
    paymentDate: "",
    total: 0,
    remark: "",
  };
  const [updateOrder, setUpdateOrder] = useState(defaultData);

  // const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!orderDetail) return;

    setUpdateOrder({
      ...defaultData,
      total: orderDetail?.payment?.total,
      remark: orderDetail?.payment?.remark,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  const isFormValid = ["paymentProof", "paymentDate", "total"].every(
    (required) => {
      let objVal = updateOrder[required];
      return required === "paymentDate" ? objVal !== "" : checkNotEmpty(objVal);
    },
  );

  const handleSlipImage = (fileCallBlack) => {
    const dataUp = updateOrder;

    setUpdateOrder({ ...dataUp, paymentProof: fileCallBlack });
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const updateParams = {
        ...updateOrder,
      };

      await updateStatusRefund(orderDetail?._id, updateParams);
      enqueueSnackbar(`Update Refund Payment Success!`, {
        variant: "success",
      });
      await onSuccess();
    } catch (err) {
      console.error("err:", err?.response);
      enqueueSnackbar(err?.response?.data?.message, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          flexDirection: {
            sm: "row",
            xs: "column",
          },
          alignItems: {
            sm: "center",
            xs: "flex-start",
          },
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
          span: {
            color: Primary,
            fontWeight: 400,
          },
        }}
      >
        หลักฐานการคืนเงิน
        {!isFormValid ? <span>โปรดแนบหลักฐานการคืนเงิน</span> : null}
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={{ sm: 3, xs: 2 }}>
          <Grid item xs={12}>
            <WrapperBox row>
              <SpanTitle bold>จำนวนเงินที่ต้องคืน</SpanTitle>

              <SpanFocusPrice>
                {convertIntToCurrency(orderDetail?.payment?.total)}{" "}
                <span>บาท</span>
              </SpanFocusPrice>
            </WrapperBox>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} container spacing={2}>
            <Grid item xs={12}>
              <SpanRemark>
                {`- ชื่อบัญชีที่รับเงินคืนจะต้องเป็นชื่อเดียวกับชื่อที่โอนเข้าบริษัทฯเท่านั้น`}
              </SpanRemark>
              <SpanRemark>{`- ใช้เวลาดำเนินการ 7-14 วันทำการ`}</SpanRemark>
            </Grid>

            {/* <Grid item xs={12}>
              <WrapperBox>
                <SpanTitle>ช่องทางการชำระเงิน</SpanTitle>

                <FormControl
                  sx={{ minWidth: 220, alignSelf: "flex-end" }}
                  error={!checkNotEmpty(updateOrder?.paymentType)}
                >
                  <StyledSelect
                    labelId="payment-type-select-label"
                    id="payment-type-select"
                    displayEmpty
                    value={updateOrder.paymentType || ""}
                    onChange={(e) => {
                      setUpdateOrder({
                        ...updateOrder,
                        paymentType: e.target.value,
                      });
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 4,
                          ".MuiList-root": { padding: 0 },
                          ".MuiMenuItem-root": {
                            minHeight: { sm: 48, xs: 40 },
                            px: { sm: 2, xs: 1.5 },
                            py: { sm: 1.5, xs: 1 },
                            fontSize: { sm: font.size.md, xs: font.size.sm },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem disabled value="" sx={{ display: "none" }}>
                      <em>เลือกช่องทางการชำระเงิน</em>
                    </MenuItem>
                    {paymentTypes.map((option) => (
                      <MenuItem value={option.value}>{option.label}</MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              </WrapperBox>
            </Grid> */}

            <Grid item xs={12}>
              <WrapperBox>
                <SpanTitle>วันที่ชำระเงิน</SpanTitle>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <StyledDatePicker
                    inputFormat="DD/MM/YYYY"
                    value={updateOrder?.paymentDate}
                    adapterLocale="th"
                    onChange={(value) => {
                      setUpdateOrder({ ...updateOrder, paymentDate: value.$d });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        variant="outlined"
                        sx={{
                          // my: 2,
                          maxWidth: 220,
                        }}
                        error={updateOrder?.paymentDate === ""}
                      />
                    )}
                  />
                </LocalizationProvider>
              </WrapperBox>
            </Grid>

            <Grid item xs={12}>
              <UploadBox>
                <WrapperUpload>
                  {checkNotEmpty(updateOrder?.paymentProof) ? (
                    <>
                      <IconButton
                        sx={{
                          padding: "3px",
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          zIndex: 10,
                          backgroundColor: "#d9d9d999",
                          ":hover": { backgroundColor: "#d9d9d9db" },
                        }}
                        onClick={() => {
                          handleSlipImage("");
                        }}
                      >
                        <CloseIcon
                          sx={{ color: "#e04132", cursor: "pointer" }}
                        />
                      </IconButton>

                      <UploadImage
                        callBackFile={(fileCallBlack) => {
                          handleSlipImage(fileCallBlack);
                        }}
                        closeAlert={false}
                      >
                        <WrapperUploadImage>
                          <WrapperImgSlip>
                            <ImgSlip
                              src={updateOrder?.paymentProof?.fileData}
                              alt="paymentProof"
                            />
                          </WrapperImgSlip>
                        </WrapperUploadImage>
                      </UploadImage>
                    </>
                  ) : (
                    <>
                      <UploadImage
                        callBackFile={(fileCallBlack) => {
                          handleSlipImage(fileCallBlack);
                        }}
                        closeAlert={false}
                      >
                        <WrapperUploadImage>
                          <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<AddIcon />}
                          >
                            แบบหลักฐานการชำระเงิน
                          </Button>
                        </WrapperUploadImage>
                      </UploadImage>

                      <WarningText sx={{ mt: "14px" }}>
                        <ErrorIcon sx={{ color: Yellow }} />

                        <span>ขนาดไฟล์สูงสุดไม่เกิน 4 MB</span>
                      </WarningText>
                    </>
                  )}
                </WrapperUpload>
              </UploadBox>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={() => onSubmit()}
          disabled={!isFormValid}
          fullWidth
          sx={{ maxWidth: 180 }}
          type="submit"
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default RefundSuccessDialog;
